import extend from 'lodash-es/extend';
import { forkJoin, from, Observable, of } from 'rxjs';
import { catchError, concatMap, timeout } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MessageResourceManager } from './message-resource-manager';
import {
    LocalstorageHelper,
    LocalStorageService,
    OnlineService,
    SerializabledDataType,
    TIMEOUT_CACHE
} from '@nts/std/utility';

export enum TranslationLoaderFileType {
    StdDescriptions = 'stdDescriptions',
    StdMessages = 'stdMessages',
    StdCustomResource = 'stdCustomResource',
    Descriptions = 'descriptions',
    Messages = 'messages',
    CustomResource = 'customResource'
}

export const TRANSLATION_LOADER_STORAGE_KEY = 'translations';
export const BASE_PATH = 'assets/i18n/';
export const BASE_FWK_PATH = `${BASE_PATH}std/`;

export const DESCRIPTIONS_FILE = '/descriptions.json';
export const MESSAGES_FILE = '/messages.json';
export const CUSTOM_RESOURCE_FILE = '/custom-resource.json';

type TranslationMap = { [key: string]: { [key: string]: string; }; };

@Injectable({
    providedIn: 'root',
})
export class TranslationLoader {
    constructor(
        private http: HttpClient,
        private onlineService: OnlineService,
        private localStorageService: LocalStorageService
    ) { }

    static getTranslationAssetKey(
        translationLoaderFileType: TranslationLoaderFileType,
        lang: string
    ) {
        return `${translationLoaderFileType}_${lang}`;
    }

    getTranslation(lang: string, fallbackLang: string = 'it'): Observable<TranslationMap> {
        lang = lang.substring(0, 2).toLowerCase();
        fallbackLang = fallbackLang.substring(0, 2).toLowerCase();

        const storageKey = `${TRANSLATION_LOADER_STORAGE_KEY}_${lang}_${fallbackLang}`;

        return from(this.localStorageService.init()).pipe(
            concatMap(() =>
                from(
                    LocalstorageHelper.getStorageItem(
                        storageKey,
                        undefined,
                        false,
                        false,
                        false
                    )
                )
            ),
            concatMap((storedTranslations) =>
                this.loadTranslations(lang, fallbackLang, storedTranslations)
            ),
            concatMap((translations) =>
                from(this.persistAndMapTranslations(storageKey, translations, lang, fallbackLang))
            )
        );
    }

    private loadTranslations(
        lang: string,
        fallbackLang: string,
        storedTranslations: SerializabledDataType
    ): Observable<TranslationMap> {
        if (this.onlineService.isOnline === false) {
            return of((storedTranslations ?? {}) as TranslationMap);
        }

        const request$ = forkJoin(this.buildAssets(lang, fallbackLang)).pipe(
            catchError(() => of((storedTranslations ?? {}) as TranslationMap))
        );

        if (storedTranslations) {
            return request$.pipe(
                timeout({
                    first: TIMEOUT_CACHE,
                    with: () => of(storedTranslations as TranslationMap),
                })
            );
        }

        return request$;
    }

    private buildAssets(
        lang: string,
        fallbackLang: string
    ): Record<string, Observable<{ [key: string]: string; }>> {
        const fallbackAssets: Record<string, Observable<{ [key: string]: string; }>> = {
            [TranslationLoader.getTranslationAssetKey(TranslationLoaderFileType.StdDescriptions, fallbackLang)]:
                this.loadAsset(BASE_FWK_PATH + fallbackLang + DESCRIPTIONS_FILE),
            [TranslationLoader.getTranslationAssetKey(TranslationLoaderFileType.StdMessages, fallbackLang)]:
                this.loadAsset(BASE_FWK_PATH + fallbackLang + MESSAGES_FILE),
            [TranslationLoader.getTranslationAssetKey(TranslationLoaderFileType.StdCustomResource, fallbackLang)]:
                this.loadAsset(BASE_FWK_PATH + fallbackLang + CUSTOM_RESOURCE_FILE),
            [TranslationLoader.getTranslationAssetKey(TranslationLoaderFileType.Descriptions, fallbackLang)]:
                this.loadAsset(BASE_PATH + fallbackLang + DESCRIPTIONS_FILE),
            [TranslationLoader.getTranslationAssetKey(TranslationLoaderFileType.Messages, fallbackLang)]:
                this.loadAsset(BASE_PATH + fallbackLang + MESSAGES_FILE),
            [TranslationLoader.getTranslationAssetKey(TranslationLoaderFileType.CustomResource, fallbackLang)]:
                this.loadAsset(BASE_PATH + fallbackLang + CUSTOM_RESOURCE_FILE),
        };

        if (fallbackLang === lang) {
            return fallbackAssets;
        }

        const langAssets: Record<string, Observable<{ [key: string]: string; }>> = {
            [TranslationLoader.getTranslationAssetKey(TranslationLoaderFileType.StdDescriptions, lang)]:
                this.loadAsset(BASE_FWK_PATH + lang + DESCRIPTIONS_FILE),
            [TranslationLoader.getTranslationAssetKey(TranslationLoaderFileType.StdMessages, lang)]:
                this.loadAsset(BASE_FWK_PATH + lang + MESSAGES_FILE),
            [TranslationLoader.getTranslationAssetKey(TranslationLoaderFileType.StdCustomResource, lang)]:
                this.loadAsset(BASE_FWK_PATH + lang + CUSTOM_RESOURCE_FILE),
            [TranslationLoader.getTranslationAssetKey(TranslationLoaderFileType.Descriptions, lang)]:
                this.loadAsset(BASE_PATH + lang + DESCRIPTIONS_FILE),
            [TranslationLoader.getTranslationAssetKey(TranslationLoaderFileType.Messages, lang)]:
                this.loadAsset(BASE_PATH + lang + MESSAGES_FILE),
            [TranslationLoader.getTranslationAssetKey(TranslationLoaderFileType.CustomResource, lang)]:
                this.loadAsset(BASE_PATH + lang + CUSTOM_RESOURCE_FILE),
        };

        return {
            ...fallbackAssets,
            ...langAssets,
        };
    }

    private loadAsset(path: string): Observable<{ [key: string]: string; }> {
        return this.http.get<{ [key: string]: string; }>(path).pipe(
            catchError(() => of({}))
        );
    }

    private async persistAndMapTranslations(
        storageKey: string,
        translations: TranslationMap,
        lang: string,
        fallbackLang: string
    ): Promise<TranslationMap> {
        if (translations && Object.keys(translations).length > 0) {
            await LocalstorageHelper.setStorageItem(
                storageKey,
                translations,
                undefined,
                false,
                false
            );
        }

        TranslationLoader.mapTranslations(translations, lang, fallbackLang);
        return translations;
    }

    static mapTranslations(
        res: { [key: string]: { [key: string]: string; }; },
        lang: string,
        fallbackLang: string = 'it'
    ) {
        lang = lang.substring(0, 2).toLowerCase();
        fallbackLang = fallbackLang.substring(0, 2).toLowerCase();

        const differentLanguage = lang != fallbackLang;

        let r: { [key: string]: string; } = {};

        if (differentLanguage) {
            r = extend(
                {},
                res[TranslationLoader.getTranslationAssetKey(TranslationLoaderFileType.StdDescriptions, fallbackLang)],
                res[TranslationLoader.getTranslationAssetKey(TranslationLoaderFileType.StdDescriptions, lang)],
                res[TranslationLoader.getTranslationAssetKey(TranslationLoaderFileType.StdMessages, fallbackLang)],
                res[TranslationLoader.getTranslationAssetKey(TranslationLoaderFileType.StdMessages, lang)],
                res[TranslationLoader.getTranslationAssetKey(TranslationLoaderFileType.StdCustomResource, fallbackLang)],
                res[TranslationLoader.getTranslationAssetKey(TranslationLoaderFileType.StdCustomResource, lang)]
            );
        } else {
            r = extend(
                {},
                res[TranslationLoader.getTranslationAssetKey(TranslationLoaderFileType.StdDescriptions, lang)],
                res[TranslationLoader.getTranslationAssetKey(TranslationLoaderFileType.StdMessages, lang)],
                res[TranslationLoader.getTranslationAssetKey(TranslationLoaderFileType.StdCustomResource, lang)]
            );
        }

        Object.keys(r).forEach(k => {
            const nk = 'std_' + k;
            r[nk] = r[k];
            delete r[k];
        });

        if (differentLanguage) {
            return MessageResourceManager.Current.translations = extend(
                {},
                r,
                res[TranslationLoader.getTranslationAssetKey(TranslationLoaderFileType.Descriptions, fallbackLang)],
                res[TranslationLoader.getTranslationAssetKey(TranslationLoaderFileType.Descriptions, lang)],
                res[TranslationLoader.getTranslationAssetKey(TranslationLoaderFileType.Messages, fallbackLang)],
                res[TranslationLoader.getTranslationAssetKey(TranslationLoaderFileType.Messages, lang)],
                res[TranslationLoader.getTranslationAssetKey(TranslationLoaderFileType.CustomResource, fallbackLang)],
                res[TranslationLoader.getTranslationAssetKey(TranslationLoaderFileType.CustomResource, lang)],
            );
        }

        return MessageResourceManager.Current.translations = extend(
            {},
            r,
            res[TranslationLoader.getTranslationAssetKey(TranslationLoaderFileType.Descriptions, lang)],
            res[TranslationLoader.getTranslationAssetKey(TranslationLoaderFileType.Messages, lang)],
            res[TranslationLoader.getTranslationAssetKey(TranslationLoaderFileType.CustomResource, lang)],
        );
    }
}
